/* eslint-disable no-console */
import { Agent } from '@models/agent.model';
import { Page } from '@models/page';
import { SystemInfo } from '@modules/agent/models/system-info.model';
import { EnvironmentResult } from '@testsigmainc/models';
import { EnvironmentResultService } from '@modules/test-development/services';
import { AgentService } from '@services/agent.service';
import { ExecutionResultService } from '@services/execution-result.service';
import { TestCaseResultService } from '@services/test-case-result.service';
import { toastify, useTranslation } from '@testsigma/atoms';
import { useCallback, useContext, useState } from 'react';
import { ResultConstant, StatusConstant } from '@testsigmainc/constants';
import { TestCaseResult } from '@testsigmainc/models';
import { SessionContext } from '@contexts/session-context';
import MobileInspectionService from '@modules/test-development/pages/mobile-inspector/services/mobile-inspections';
import { System } from '@models/system.model';
import { logErrorToSentry } from '@utils/common';
import { isDebuggingWithDrifterSupported } from '@modules/test-development/components/cases/ad-hoc-runs/debugger/utils';
import {
  DrifterService,
  HealthCheckResponse,
  AgentStatus,
} from '../../../services/drifter.service.js';

const useDebuggerRunButton = (version) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [isAlreadyDebuggerSessionFound, setIsAlreadyDebuggerSessionFound] =
    useState<EnvironmentResult>();
  const [showDebugRunForm, setShowDebugRunForm] = useState(false);
  const [isDebuggerAvailable, setIsDebuggerAvailable] = useState(false);
  const [systemInfo, setSystemInfo] = useState<SystemInfo>();
  const [agentInfo, setAgentInfo] = useState<System>();
  const [testCaseResult, setTestCaseResult] = useState<TestCaseResult>();
  const [stopOnly, setStopOnly] = useState(false);

  const sessionContext = useContext(SessionContext);
  const tenant = sessionContext?.session?.tenant;

  const { t } = useTranslation();

  const fetchAgentByTargetMachine = (targetMachineId: number) => {
    AgentService.findAll(`systemId:${targetMachineId}`).then((res) => {
      const agent = res?.content?.[0];
      if (agent?.system) setAgentInfo(agent.system);
    });
  };

  const getTestCaseResultByExecutionResultId = (executionResultId: number) => {
    TestCaseResultService.findAll(
      `executionResultId:${executionResultId}`,
    ).then((res) => {
      const tcr = res?.content?.[0];
      if (tcr) {
        setTestCaseResult(tcr);
        const targetMachineId =
          tcr?.environmentResult?.executionEnvironment?.targetMachine ||
          tcr?.environmentResult?.targetMachineId;
        if (targetMachineId) {
          fetchAgentByTargetMachine(targetMachineId);
        }
      }
    });
  };

  const stopExecution = (refresh?: () => void) => {
    setIsStopping(true);
    const executionResultId =
      isAlreadyDebuggerSessionFound?.executionResult?.id;
    ExecutionResultService.show(+executionResultId)
      .then(async (executionResult) => {
        executionResult.result = ResultConstant.STOPPED;
        executionResult.status = StatusConstant.STATUS_COMPLETED;
        await ExecutionResultService.stop(executionResult.id);
        if (
          isAlreadyDebuggerSessionFound?.executionEnvironment?.runInMobileDebug
        ) {
          const payload = {
            id: isAlreadyDebuggerSessionFound?.mobileInspectionId,
            status: 'FINISHED',
            stoppedUserId: tenant?.id,
          };
          await MobileInspectionService.deleteSession(
            isAlreadyDebuggerSessionFound?.mobileInspectionId,
            payload,
          );
          setShowDebugRunForm(true);
          setIsAlreadyDebuggerSessionFound(null);
          setIsChecking(false);
          setSystemInfo(null);
          DrifterService.stopMobileSession().catch((error) => {
            console.error('Failed to clear mobile information:', error);
          });
          toastify(t('debugging_session.detected.stoped.success'), {
            type: 'success',
          });
        } else {
          AgentService.stopExecution(tenant?.id, testCaseResult?.id).then(
            () => {
              setShowDebugRunForm(true);
              setIsAlreadyDebuggerSessionFound(null);
              setIsChecking(false);
              setSystemInfo(null);
              DrifterService.stopMobileSession().catch((error) => {
                console.error('Failed to clear mobile information:', error);
              });
              toastify(t('debugging_session.detected.stoped.success'), {
                type: 'success',
              });
            },
          );
        }
        DrifterService.stopDebuggingSession(tenant?.id, testCaseResult?.id);
      })
      .catch((err) => {
        logErrorToSentry({ error: err });
        toastify(t('run.stop-failed'), {
          type: 'error',
        });
      })
      .finally(() => {
        setIsStopping(false);
        refresh?.();
      });
  };

  const closeAlreadyDebuggerSessionFound = () => {
    setIsAlreadyDebuggerSessionFound(null);
    setIsChecking(false);
    setShowDebugRunForm(false);
    setSystemInfo(null);
    setAgentInfo(undefined);
  };

  const checkExistingDebuggerSession = useCallback((agentUuid: string) => {
    setStopOnly(false);
    return new Promise((resolve, reject) => {
      EnvironmentResultService.findAll(
        `agentUuid:${agentUuid},result@RUNNING#PAUSED`,
        null,
        1000,
      )
        .then((res: Page<EnvironmentResult>) => {
          if (res?.content?.length) {
            let isHaveRunningDebuggerSession = false;
            res.content.forEach((environmentResultItem) => {
              if (
                (environmentResultItem.isRunning ||
                  environmentResultItem.isPaused ||
                  environmentResultItem.isExecuting) &&
                (environmentResultItem.executionEnvironment.runInMobileDebug ||
                  environmentResultItem.executionEnvironment.runInWebDebug)
              ) {
                isHaveRunningDebuggerSession = true;
                setIsAlreadyDebuggerSessionFound(environmentResultItem);
                getTestCaseResultByExecutionResultId(
                  environmentResultItem.executionResult.id,
                );
                resolve(true);
                return;
              }
            });
            if (isHaveRunningDebuggerSession) {
              setShowDebugRunForm(false);
              setIsChecking(false);
            } else {
              setShowDebugRunForm(true);
              setIsChecking(false);
              setIsAlreadyDebuggerSessionFound(null);
            }
          } else {
            setIsChecking(false);
            setIsAlreadyDebuggerSessionFound(null);
            setShowDebugRunForm(true);
          }
          resolve(true);
        })
        .catch((error) => {
          setIsChecking(false);
          setIsAlreadyDebuggerSessionFound(null);
          setShowDebugRunForm(true);
          reject(error);
        });
    });
  }, []);

  const checkDebuggerReadyStatus = useCallback(() => {
    return new Promise((resolve, reject) => {
      DrifterService.checkDrifterHealth()
        .then((res: HealthCheckResponse) => {
          if (!res) {
            reject(new Error('Testsigma Terminal agent is not reachable'));
            return;
          }
          const hasNewAgentStatus = res.javaAgentStatus !== AgentStatus.UNKNOWN;
          const isHealthy = hasNewAgentStatus
            ? res.javaAgentStatus === AgentStatus.ONLINE
            : true;

          if (isHealthy) {
            setIsDebuggerAvailable(true);
            resolve(true);
          } else {
            reject(new Error('Testsigma Terminal agent is not online'));
          }
        })
        .catch(reject);
    });
  }, []);

  const testSigmaTerminalFlow = useCallback(
    async (agentUuid: string) => {
      try {
        await checkExistingDebuggerSession(agentUuid);
        // checkDebuggerReadyStatus to getRunForm doesn't flash the offline form
        setIsChecking(true);
        await checkDebuggerReadyStatus();
      } catch (error) {
        throw error;
      }
    },
    [checkExistingDebuggerSession, checkDebuggerReadyStatus],
  );

  const legacyFlow = useCallback(async () => {
    try {
      const systemInfo = await AgentService.ping();
      if (!systemInfo) {
        throw new Error('Agent is not reachable');
      }
      setSystemInfo(systemInfo);
      const result = await AgentService.findByUuid(systemInfo.uniqueId);
      if (!result.system) {
        throw new Error('Agent information is not available via uuid');
      }
      setAgentInfo(result.system);
      return { systemInfo, agentInfo: result.system };
    } catch (error) {
      logErrorToSentry({ error: error });
      setIsChecking(false);
      setIsDebuggerAvailable(false); // Agent unreachable → debugger not available
      setShowDebugRunForm(true);
      throw error;
    }
  }, []);

  const handleClick = useCallback(async () => {
    try {
      setIsChecking(true);
      setIsDebuggerAvailable(false);

      // Legacy flow to get system info we shoiuld get the agent id from termial resposne for new flow.
      const { systemInfo } = await legacyFlow();

      const hasDrifterSupport = isDebuggingWithDrifterSupported(
        version?.application?.applicationType,
      );

      if (hasDrifterSupport) {
        try {
          await testSigmaTerminalFlow(systemInfo.uniqueId);
        } catch (error) {
          throw error;
        }
      } else {
        await checkExistingDebuggerSession(systemInfo.uniqueId);
      }
    } catch {
      // On error (agent unreachable, terminal not online, etc.), show the form
      // as a fallback so the user isn't stuck on a blank/loading state.
      setShowDebugRunForm(true);
    } finally {
      // Always stop the loading indicator. Don't touch showDebugRunForm here —
      // checkExistingDebuggerSession already sets it correctly on success,
      // and the catch block handles the error fallback.  
      setIsChecking(false);
    }
  }, [
    legacyFlow,
    version?.application?.applicationType,
    testSigmaTerminalFlow,
    checkExistingDebuggerSession,
  ]);

  const handleCheckRunningDebuggerSession = (session: any) => {
    setStopOnly(true);
    setIsAlreadyDebuggerSessionFound({
      ...session,
      executionResult: { id: session.testPlanResultId },
    });
    getTestCaseResultByExecutionResultId(session.testPlanResultId);

    ExecutionResultService.show(session.testPlanResultId)
      .then((executionResult) => {
        setIsAlreadyDebuggerSessionFound((prev) => ({
          ...prev,
          executionResult,
        }));
      })
      .catch((e) => {
        console.log('Failed to fetch execution result:', e);
      });

    const hybridDevice = session?.testDevices?.find(
      (td: any) => td.isHybrid && td.targetMachine,
    );
    if (hybridDevice) {
      AgentService.findAll('systemId:' + hybridDevice.targetMachine)
        .then((res: Page<Agent>) => {
          if (res?.content?.length) {
            setAgentInfo(res.content[0].system);
          }
        })
        .catch((e) => {
          console.log('Failed to fetch agent info:', e);
        });
    }
  };

  return {
    isChecking,
    showDebugRunForm,
    isDebuggerAvailable,
    setShowDebugRunForm,
    isAlreadyDebuggerSessionFound,
    systemInfo,
    handleClick,
    closeAlreadyDebuggerSessionFound,
    stopExecution,
    isStopping,
    testCaseResult,
    agentInfo,
    stopOnly,
    setIsAlreadyDebuggerSessionFound,
    checkRunningDebuggerSession: handleCheckRunningDebuggerSession,
  };
};

export default useDebuggerRunButton;
